/**
 * Stream tickets — how the browser opens an `EventSource` (or an embedded download) WITHOUT the JWT in the URL.
 *
 * `EventSource` and `<iframe>` cannot send `Authorization`, so the access token used to be spliced onto the
 * URL as `?token=<JWT>`: into the proxy's access log, the browser history and `Referer`, for its whole
 * remaining lifetime. The backend no longer reads it (see `platform/auth/stream-ticket.service.ts`).
 *
 * Instead the client asks `POST /api/auth/stream-ticket` — authenticated with the normal Bearer header — for a
 * TICKET: opaque, valid 60 s, SINGLE-USE and bound to one route. The URL carries only that. Because it is
 * single-use, native `EventSource` auto-reconnect (which replays the same URL) no longer works: whoever opens a
 * stream must reconnect by asking for a NEW ticket. `connectTicketedEventSource` does exactly that.
 */
import { apiService } from './api.service'

/** What a ticket is for — mirrors `STREAM_TICKET_PURPOSES` on the server. */
export type StreamTicketPurpose =
  | 'balance-updates'
  | 'sync-stream'
  | 'market-data-jobs'
  | 'performance-metrics'
  | 'tax-report-pdf'
  | 'websocket'

interface StreamTicketResponse {
  ticket: string
  expiresInSeconds: number
}

/** A fresh single-use ticket, or `null` when the caller is not signed in / the request fails. */
export async function requestStreamTicket(
  purpose: StreamTicketPurpose,
  resourceId?: string,
): Promise<string | null> {
  try {
    const response = await apiService.post<StreamTicketResponse>('/api/auth/stream-ticket', {
      purpose,
      ...(resourceId ? { resourceId } : {}),
    })
    return response.data?.ticket ?? null
  } catch {
    return null
  }
}

/** `path` with a fresh `ticket` query parameter, or `null` when no ticket could be obtained. */
export async function streamUrl(
  path: string,
  purpose: StreamTicketPurpose,
  resourceId?: string,
): Promise<string | null> {
  const ticket = await requestStreamTicket(purpose, resourceId)
  if (!ticket) return null
  return `${path}${path.includes('?') ? '&' : '?'}ticket=${encodeURIComponent(ticket)}`
}

const INITIAL_RECONNECT_DELAY_MS = 1_000
const MAX_RECONNECT_DELAY_MS = 30_000

export interface TicketedStreamOptions {
  purpose: StreamTicketPurpose
  path: string
  withCredentials?: boolean
  /** Attach listeners to EVERY new EventSource (each reconnect makes a new one). */
  setup: (source: EventSource) => void
  /** Called when the stream drops, before the reconnect is scheduled. */
  onDisconnect?: () => void
}

/**
 * An `EventSource` that survives drops: on `error` it closes, asks for a NEW ticket and reconnects with
 * exponential backoff (1 s … 30 s). Returns `close()`, which also cancels a pending reconnect or ticket request.
 */
export function connectTicketedEventSource(options: TicketedStreamOptions): { close: () => void } {
  let closed = false
  let source: EventSource | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let attempt = 0

  const open = async (): Promise<void> => {
    const url = await streamUrl(options.path, options.purpose)
    if (closed) return
    if (!url) {
      schedule()
      return
    }
    const next = new EventSource(url, { withCredentials: options.withCredentials ?? false })
    source = next
    next.addEventListener('open', () => {
      attempt = 0
    })
    next.addEventListener('error', () => {
      next.close()
      if (source === next) source = null
      options.onDisconnect?.()
      schedule()
    })
    options.setup(next)
  }

  const schedule = (): void => {
    if (closed) return
    const delay = Math.min(INITIAL_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS)
    attempt += 1
    timer = setTimeout(() => {
      timer = null
      void open()
    }, delay)
  }

  void open()

  return {
    close: () => {
      closed = true
      if (timer) clearTimeout(timer)
      source?.close()
      source = null
    },
  }
}
